import { useGetApiV1ExamExamitemidPreview } from '@/shared/api/generated';

// ─── Tipos ────────────────────────────────────────────────────────────────────

/** Alternativa exibida no preview da questão */
export interface PreviewAlternative {
  readonly id: string;
  readonly value: string;
  readonly isCorrect: boolean;
}

/** Dados de preview de uma questão */
export interface QuestionPreviewData {
  readonly id: string;
  readonly text: string;
  readonly componentType: string;
  readonly alternatives: PreviewAlternative[];
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Hook para visualização de preview de uma questão da Biblioteca de Questões.
 *
 * Carrega os dados da questão via `useGetApiV1ExamExamitemidPreview`.
 * Só dispara a requisição quando `questionId` for não-nulo.
 *
 * @param questionId - ID da questão (string) ou null quando nenhuma questão está selecionada
 * @returns Dados de preview, estado de loading/error
 *
 * @example
 * ```tsx
 * const { preview, isLoading } = useQuestionPreview(selectedId);
 * ```
 */
export function useQuestionPreview(questionId: string | null) {
  const numericId = questionId ? Number(questionId) : 0;

  const query = useGetApiV1ExamExamitemidPreview(numericId, {
    query: { enabled: !Number.isNaN(numericId) && numericId > 0 },
  });

  const preview: QuestionPreviewData | null = query.data
    ? {
        id: String(query.data.id ?? ''),
        text: query.data.examItem?.label ?? '',
        componentType: query.data.examItem?.componentType ?? '',
        alternatives: (query.data.examTypeList ?? []).map(a => ({
          id: String(a.id ?? ''),
          value: a.value ?? '',
          isCorrect: a.checked ?? false,
        })),
      }
    : null;

  return {
    preview,
    isLoading: query.isPending,
    isError: query.isError,
  } as const;
}
